import { ReactElement, useState } from 'react';
import styles from './chart-container.module.scss';
import {
    UIErrorState,
    UIIcon,
    UIIconButton,
    UIIconName,
    UILoadingState,
    UIMenu,
    UIMenuItem,
    UIMenuItemProps,
    UIModal,
    UIModalContent,
    UIModalFooter,
    UIModalHeader,
    UIPopover,
    UIPressableAreaButtonLess,
    UIText,
    UITextType
} from '@/ui';
import { charcoal } from '@/ui/styles';
import { useAnalyticsTranslation } from '../hooks';

type ChartContainerProps = {
    title: string;
    titleWeight?: 'normal' | 'medium' | 'bold';
    subtitle?: string | null | undefined;
    subtitleType?: UITextType;
    subtitleIcon?: UIIconName;
    subtitleIconColor?: string;
    rightMenuText?: string;
    rightMenuItems?: UIMenuItemProps[];
    description: string;
    loading?: boolean;
    loadingText?: string;
    error?: boolean;
    errorText?: string;
    style?: React.CSSProperties;
    children: React.ReactNode;
    hideHeader?: boolean;
    contentPadding?: number;
};

export function ChartContainer(props: ChartContainerProps): ReactElement {
    const { t } = useAnalyticsTranslation();
    const {
        title,
        titleWeight = 'normal',
        subtitle,
        subtitleType = 'default2',
        subtitleIcon,
        subtitleIconColor = charcoal,
        rightMenuText = '',
        rightMenuItems = [],
        description,
        loading,
        loadingText,
        error,
        errorText,
        style,
        children,
        hideHeader = false,
        contentPadding = 16
    } = props;
    const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);

    return (
        <>
            <div className={styles.container} style={style}>
                {!hideHeader && (
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            paddingLeft: 16,
                            paddingRight: 16,
                            paddingTop: 16
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 6
                            }}
                        >
                            <UIText
                                variant="basic"
                                medium={titleWeight === 'medium'}
                                bold={titleWeight === 'bold'}
                            >
                                {title}
                            </UIText>
                            {subtitle && (
                                <div
                                    style={{
                                        display: 'flex',
                                        gap: 4,
                                        alignItems: 'center'
                                    }}
                                >
                                    {subtitleIcon && (
                                        <UIIcon
                                            name={subtitleIcon}
                                            size={16}
                                            color={subtitleIconColor}
                                        />
                                    )}
                                    <UIText variant="small" type={subtitleType}>
                                        {subtitle}
                                    </UIText>
                                </div>
                            )}
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                            {rightMenuItems.length > 0 && (
                                <UIPopover
                                    placement="bottom_left"
                                    popoverPadding={4}
                                    trigger="click"
                                    triggerNode={
                                        <button
                                            className={styles.removeButtonStyle}
                                        >
                                            <UIPressableAreaButtonLess
                                                verticalPadding={6}
                                                horizontalPadding={10}
                                            >
                                                <UIText>{rightMenuText}</UIText>
                                                <UIIcon
                                                    name={'chevron-down'}
                                                    size={18}
                                                    color={charcoal}
                                                />
                                            </UIPressableAreaButtonLess>
                                        </button>
                                    }
                                >
                                    <UIMenu>
                                        {rightMenuItems.map(
                                            (menuItemProps, index) => (
                                                <UIMenuItem
                                                    key={index}
                                                    {...menuItemProps}
                                                />
                                            )
                                        )}
                                    </UIMenu>
                                </UIPopover>
                            )}

                            <UIIconButton
                                icon="info"
                                onClick={() => setIsInfoModalOpen(true)}
                                small
                            />
                        </div>
                    </div>
                )}

                {loading ? (
                    <UILoadingState text={loadingText} />
                ) : error ? (
                    <UIErrorState
                        title={errorText ?? t('shared.errors.failed_to_load')}
                    />
                ) : (
                    <div
                        style={{
                            display: 'flex',
                            flex: 1,
                            paddingLeft: contentPadding,
                            paddingBottom: contentPadding,
                            paddingRight: contentPadding
                        }}
                    >
                        {children}
                    </div>
                )}
            </div>
            <UIModal
                isOpen={isInfoModalOpen}
                onClose={() => {
                    setIsInfoModalOpen(false);
                }}
            >
                <UIModalHeader
                    title={t('components.chart_container.info_title', {
                        title
                    })}
                />
                <UIModalContent>
                    <UIText>{description}</UIText>
                </UIModalContent>
                <UIModalFooter
                    actions={[
                        {
                            text: t('shared.labels.close'),
                            onClick: () => {
                                setIsInfoModalOpen(false);
                            }
                        }
                    ]}
                />
            </UIModal>
        </>
    );
}
